import { useAuth } from "@elit-hair/elit-tools";
import { Button, EmptyState, ErrorState, Icon, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { TbActivity, TbCurrencyDollar, TbLockOpen, TbPlus, TbSparkles, TbTargetArrow } from "react-icons/tb";
import { toast } from "sonner";

import { PageActions } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { SearchBar } from "@/components/ui/search-bar";
import { PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { AnalyserAccessDialog } from "./components/analyser-access-dialog";
import { AnalyserCard } from "./components/analyser-card";
import { AnalyserLogsSheet } from "./components/analyser-logs-sheet";
import { ANALYSER_STATUS_STYLES } from "./components/analyser-ui";
import { AnalyserVersionsDialog } from "./components/analyser-versions-dialog";
import { NEW_ANALYSER_ID } from "./constants/analyser.constants";
import { useAnalyserUsers } from "./hooks/use-analyser-users";
import {
    useCallAnalyserModels,
    useCallAnalysers,
    useRestoreCallAnalyserVersion,
    useSetCallAnalyserEditors,
} from "./queries/call-analyser.queries";
import type { AnalyserStatus } from "./types/analyser.types";
import { formatCostUsd } from "./utils/analyser-stats";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_ANALYSERS)({
    component: AnalysersPage,
    staticData: {
        config: {
            title: "developer_analysers_title",
            breadcrumb: { title: "developer_analysers_title", to: APP_ROUTES_TO.DEVELOPER_ANALYSERS },
        },
    },
});

type Scope = "mine" | "all";

const STATUS_ORDER: Record<AnalyserStatus, number> = { active: 0, paused: 1, draft: 2 };

function AnalysersPage() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const { data: analysers = [], isLoading, isError, refetch } = useCallAnalysers();
    const { data: models = [] } = useCallAnalyserModels();
    const restoreVersion = useRestoreCallAnalyserVersion();
    const setEditors = useSetCallAnalyserEditors();
    const [scope, setScope] = useState<Scope>("mine");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<AnalyserStatus | null>(null);
    const [versionsId, setVersionsId] = useState<string | null>(null);
    const [logsId, setLogsId] = useState<string | null>(null);
    const [accessId, setAccessId] = useState<string | null>(null);

    const { users, currentUserId } = useAnalyserUsers(
        analysers.flatMap((analyser) => [
            analyser.ownerId,
            ...analyser.editorIds,
            ...analyser.versions.map((version) => version.authorId),
        ])
    );
    const permissionCodes: string[] = userData?.permissionCodes ?? [];
    const canCreate = permissionCodes.includes(PERMISSIONS.CALL_ANALYSER_CREATE);

    const mine = analysers.filter((analyser) => analyser.canEdit);
    const scoped = scope === "mine" ? mine : analysers;
    const visibleAnalysers = scoped.filter((analyser) => {
        if (statusFilter && analyser.status !== statusFilter) {
            return false;
        }

        const term = search.trim().toLowerCase();
        return (
            !term ||
            analyser.versions[0].agent.name.toLowerCase().includes(term) ||
            analyser.description.toLowerCase().includes(term)
        );
    });
    // Active first, then the most recently changed.
    visibleAnalysers.sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.updatedAt.localeCompare(a.updatedAt)
    );
    const goToNew = () => navigate({ to: APP_ROUTES_TO.DEVELOPER_ANALYSER, params: { analyserId: NEW_ANALYSER_ID } });

    const totals = analysers.reduce(
        (sum, analyser) => ({
            runs: sum.runs + analyser.stats.total,
            success: sum.success + analyser.stats.success,
            failed: sum.failed + analyser.stats.failed,
            cost: sum.cost + analyser.stats.totalCostUsd,
        }),
        { runs: 0, success: 0, failed: 0, cost: 0 }
    );
    const findAnalyser = (id: string | null) => analysers.find((analyser) => analyser.id === id) ?? null;
    const versionsOf = findAnalyser(versionsId);
    const accessOf = findAnalyser(accessId);
    const logsOf = findAnalyser(logsId);

    const summary = [
        { label: "You can edit", value: mine.length, icon: TbLockOpen },
        {
            label: "Active analysers",
            value: analysers.filter((analyser) => analyser.status === "active").length,
            icon: TbSparkles,
        },
        { label: "Runs · last 7 days", value: totals.runs, icon: TbActivity },
        { label: "Model cost · 7d", value: formatCostUsd(totals.cost), icon: TbCurrencyDollar },
        {
            label: "Success rate",
            value:
                totals.success + totals.failed > 0
                    ? `${Math.round((totals.success / (totals.success + totals.failed)) * 100)}%`
                    : "—",
            icon: TbTargetArrow,
        },
    ];

    if (isError) {
        return (
            <AdminSurface className="p-6">
                <ErrorState
                    title="Couldn't load the call analysers"
                    message="Please try again."
                    onRetry={() => void refetch()}
                />
            </AdminSurface>
        );
    }

    return (
        <div className="space-y-4">
            <PageActions>
                <div className="w-full sm:w-72">
                    <SearchBar
                        value={search}
                        onChange={setSearch}
                        placeholder="Search analysers"
                        className="[&_input]:h-9"
                    />
                </div>
                {canCreate ? (
                    <Button onClick={goToNew}>
                        <Icon icon={TbPlus} />
                        New analyser
                    </Button>
                ) : null}
            </PageActions>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                {summary.map((item) => (
                    <AdminSurface key={item.label} className="flex items-center gap-3 p-4">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Icon icon={item.icon} size={18} />
                        </span>
                        <div className="min-w-0">
                            <p className="text-xl font-semibold tabular-nums">{item.value}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.label}</p>
                        </div>
                    </AdminSurface>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-xl border border-border/60 bg-muted/30 p-1">
                    {(["mine", "all"] as const).map((value) => (
                        <button
                            key={value}
                            type="button"
                            aria-pressed={scope === value}
                            onClick={() => setScope(value)}
                            className={cn(
                                "rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                                scope === value
                                    ? "bg-background text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            {value === "mine" ? "My analysers" : "All analysers"}
                            <span className="ml-1.5 tabular-nums opacity-60">
                                {value === "mine" ? mine.length : analysers.length}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap gap-1.5">
                    {(Object.keys(ANALYSER_STATUS_STYLES) as AnalyserStatus[]).map((status) => (
                        <button
                            key={status}
                            type="button"
                            aria-pressed={statusFilter === status}
                            onClick={() => setStatusFilter((current) => (current === status ? null : status))}
                            className={cn(
                                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                                statusFilter === status
                                    ? "border-foreground/20 bg-foreground text-background"
                                    : "border-border/70 text-muted-foreground hover:bg-muted"
                            )}
                        >
                            <span className={cn("size-1.5 rounded-full", ANALYSER_STATUS_STYLES[status].dot)} />
                            {ANALYSER_STATUS_STYLES[status].label}
                            <span className="tabular-nums opacity-60">
                                {scoped.filter((analyser) => analyser.status === status).length}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <AdminSurface key={index} className="space-y-4 p-5">
                            <div className="flex items-start gap-3">
                                <Skeleton className="size-9 rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-1/2" />
                                    <Skeleton className="h-3 w-3/4" />
                                </div>
                            </div>
                            <div className="flex gap-1.5">
                                <Skeleton className="h-5 w-20 rounded-full" />
                                <Skeleton className="h-5 w-24 rounded-full" />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <Skeleton className="h-12 rounded-xl" />
                                <Skeleton className="h-12 rounded-xl" />
                                <Skeleton className="h-12 rounded-xl" />
                            </div>
                        </AdminSurface>
                    ))}
                </div>
            ) : visibleAnalysers.length === 0 ? (
                <AdminSurface className="p-8">
                    <EmptyState
                        variant="simple"
                        icon={<TbSparkles size={40} className="text-muted-foreground" />}
                        title={
                            scope === "mine" && mine.length === 0
                                ? "You can't edit any analyser yet"
                                : "No analysers match"
                        }
                    />
                    {analysers.length === 0 && canCreate ? (
                        <div className="mt-4 flex justify-center">
                            <Button onClick={goToNew}>
                                <Icon icon={TbPlus} />
                                Create the first analyser
                            </Button>
                        </div>
                    ) : search || statusFilter ? (
                        <div className="mt-4 flex justify-center">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSearch("");
                                    setStatusFilter(null);
                                }}
                            >
                                Clear filters
                            </Button>
                        </div>
                    ) : null}
                </AdminSurface>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
                    {visibleAnalysers.map((analyser) => (
                        <AnalyserCard
                            key={analyser.id}
                            analyser={analyser}
                            stats={analyser.stats}
                            users={users}
                            models={models}
                            currentUserId={currentUserId}
                            canEdit={analyser.canEdit}
                            onEdit={() =>
                                navigate({ to: APP_ROUTES_TO.DEVELOPER_ANALYSER, params: { analyserId: analyser.id } })
                            }
                            onVersions={() => setVersionsId(analyser.id)}
                            onLogs={() => setLogsId(analyser.id)}
                            onAccess={() => setAccessId(analyser.id)}
                        />
                    ))}
                </div>
            )}

            <AnalyserVersionsDialog
                analyser={versionsOf}
                users={users}
                models={models}
                currentUserId={currentUserId}
                canEdit={versionsOf?.canEdit ?? false}
                onOpenChange={(open) => !open && setVersionsId(null)}
                onRestore={(version) => {
                    if (!versionsOf) {
                        return;
                    }
                    restoreVersion.mutate(
                        { id: versionsOf.id, version },
                        {
                            onSuccess: (analyser) =>
                                toast.success(`Version ${version} restored as version ${analyser.versions[0].version}`),
                            onError: () => toast.error("Couldn't restore the version"),
                        }
                    );
                }}
            />

            <AnalyserLogsSheet
                analyser={logsOf}
                stats={logsOf?.stats}
                onOpenChange={(open) => !open && setLogsId(null)}
            />

            <AnalyserAccessDialog
                analyser={accessOf}
                users={users}
                currentUserId={currentUserId}
                isSaving={setEditors.isPending}
                onOpenChange={(open) => !open && setAccessId(null)}
                onSave={(editorIds) => {
                    if (!accessOf) {
                        return;
                    }
                    setEditors.mutate(
                        { id: accessOf.id, editorIds },
                        {
                            onSuccess: () => {
                                toast.success("Access updated");
                                setAccessId(null);
                            },
                            onError: () => toast.error("Couldn't update the access"),
                        }
                    );
                }}
            />
        </div>
    );
}
